import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Truck, Send, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const CARRIERS = [
  { value: "ups", label: "UPS" },
  { value: "usps", label: "USPS" },
  { value: "fedex", label: "FedEx" },
  { value: "dhl", label: "DHL" },
  { value: "other", label: "Other" },
];

function Field({ label, value, testId }) {
  return (
    <div>
      <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/50">{label}</p>
      <p className="mt-1 font-mono text-xs text-[#1A1714] break-words" data-testid={testId}>
        {value == null || value === "" ? "—" : String(value)}
      </p>
    </div>
  );
}

function Address({ addr, prefix }) {
  if (!addr) return <p className="font-mono text-xs italic text-[#1A1714]/60">Not provided.</p>;
  return (
    <div className="grid grid-cols-2 gap-3">
      <Field label="Country" value={addr.country} testId={`${prefix}-country`} />
      <Field label="Full name" value={addr.full_name} testId={`${prefix}-full-name`} />
      <Field label="Line 1" value={addr.line1} testId={`${prefix}-line1`} />
      <Field label="Line 2" value={addr.line2} testId={`${prefix}-line2`} />
      <Field label="Apartment" value={addr.apartment} testId={`${prefix}-apartment`} />
      <Field label="City" value={addr.city} testId={`${prefix}-city`} />
      <Field label="State" value={addr.state} testId={`${prefix}-state`} />
      <Field label="Postal code" value={addr.postal_code} testId={`${prefix}-postal`} />
      <Field label="Landmark" value={addr.landmark} testId={`${prefix}-landmark`} />
    </div>
  );
}

export default function OrderDetailDialog({ orderId, open, onOpenChange, onChanged }) {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tracking, setTracking] = useState("");
  const [carrier, setCarrier] = useState("ups");
  const [saving, setSaving] = useState(false);

  const load = () => {
    if (!open || !orderId) return;
    setLoading(true);
    api.get(`/admin/orders/${orderId}`)
      .then((r) => {
        setOrder(r.data);
        setTracking(r.data?.tracking_number || "");
        setCarrier(r.data?.carrier || "ups");
      })
      .catch((e) => toast.error(e.response?.data?.detail || "Could not load order"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [open, orderId]);

  const saveTracking = async () => {
    const tn = tracking.trim();
    if (!tn) {
      toast.error("Enter a tracking number");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/orders/${orderId}/tracking`, {
        tracking_number: tn,
        carrier,
        send_email: true,
      });
      if (data.email_sent) {
        toast.success("Tracking saved · shipped email sent");
      } else {
        toast.success("Tracking saved · email could not be sent");
      }
      // Reload the order so status/shipped_at reflect immediately
      load();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const c = order?.contact || {};
  const shipped = order?.status === "shipped";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#F9F6F0] rounded-none border-[#E5E1D8] max-w-3xl w-[95vw] max-h-[90vh] overflow-y-auto p-0"
        data-testid="order-detail-dialog"
      >
        <DialogHeader className="p-6 border-b border-[#E5E1D8]">
          <DialogTitle className="font-serif text-3xl text-[#1A1714]">Order {orderId}</DialogTitle>
          {order && (
            <DialogDescription className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">
              {new Date(order.created_at).toLocaleString()} · {order.status} · ${(order.amount || 0).toFixed(2)}
            </DialogDescription>
          )}
        </DialogHeader>
        {loading || !order ? (
          <p className="p-8 font-serif italic text-lg text-[#1A1714]/60">Loading…</p>
        ) : (
          <div className="p-6 space-y-8" data-testid="order-detail-content">
            {/* --- Shipping / Tracking section --- */}
            <section data-testid="detail-tracking-section">
              <h3 className="font-serif text-2xl mb-3 text-[#1A1714] flex items-center gap-2">
                <Truck size={20} /> Shipping & tracking
              </h3>
              <div className="border border-[#E5E1D8] p-4 space-y-4">
                {shipped && order.shipped_at && (
                  <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60 flex items-center gap-2">
                    <CheckCircle2 size={12} className="text-[#7A2021]" />
                    Marked shipped on {new Date(order.shipped_at).toLocaleString()}
                  </p>
                )}
                <div className="grid grid-cols-1 md:grid-cols-[1fr,180px,auto] gap-3 items-end">
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">
                      Tracking number
                    </label>
                    <Input
                      value={tracking}
                      onChange={(e) => setTracking(e.target.value)}
                      placeholder="e.g. 1Z999AA10123456784"
                      className="mt-1 rounded-none border-[#E5E1D8]"
                      data-testid="tracking-input"
                    />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">
                      Carrier
                    </label>
                    <select
                      value={carrier}
                      onChange={(e) => setCarrier(e.target.value)}
                      className="mt-1 w-full h-10 border border-[#E5E1D8] rounded-none bg-white px-3 font-mono text-xs uppercase tracking-editorial"
                      data-testid="tracking-carrier"
                    >
                      {CARRIERS.map((c) => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </select>
                  </div>
                  <Button
                    onClick={saveTracking}
                    disabled={saving}
                    data-testid="tracking-save"
                    className="rounded-none bg-[#1A1714] text-[#F9F6F0] hover:bg-[#7A2021] font-mono text-xs uppercase tracking-editorial h-10"
                  >
                    <Send size={12} className="mr-2" />
                    {saving ? "Saving…" : "Save & notify"}
                  </Button>
                </div>
                <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/50">
                  Saving updates the tracking, marks the order as shipped, and automatically emails the customer a link to view live tracking on our site.
                </p>
              </div>
            </section>

            <section>
              <h3 className="font-serif text-2xl mb-3 text-[#1A1714]">Customer</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <Field label="Buyer" value={order.user_id || "Guest checkout"} testId="detail-buyer" />
                <Field label="First name" value={c.first_name} testId="detail-first-name" />
                <Field label="Last name" value={c.last_name} testId="detail-last-name" />
                <Field label="Company" value={c.company} testId="detail-company" />
                <Field label="Email" value={c.email || order.email} testId="detail-email" />
                <Field label="Mobile" value={c.mobile} testId="detail-mobile" />
                <Field label="Alt phone" value={c.alt_phone} testId="detail-alt-phone" />
              </div>
            </section>

            <section>
              <h3 className="font-serif text-2xl mb-3 text-[#1A1714]">Shipping address</h3>
              <Address addr={order.shipping_address} prefix="detail-ship" />
            </section>

            <section>
              <h3 className="font-serif text-2xl mb-3 text-[#1A1714]">
                Billing address
                {order.billing_same_as_shipping && <span className="ml-2 font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/50">(same as shipping)</span>}
              </h3>
              <Address addr={order.billing_address} prefix="detail-bill" />
            </section>

            <section>
              <h3 className="font-serif text-2xl mb-3 text-[#1A1714]">Items</h3>
              <div className="border border-[#E5E1D8]" data-testid="detail-items">
                <div className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-[#E5E1D8] font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">
                  <div className="col-span-2">SKU / ISBN</div>
                  <div className="col-span-5">Title</div>
                  <div className="col-span-2">Format</div>
                  <div className="col-span-1 text-right">Qty</div>
                  <div className="col-span-2 text-right">Line total</div>
                </div>
                {(order.items || []).map((it, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-[#E5E1D8] font-mono text-xs items-center">
                    <div className="col-span-2">
                      <div>{it.sku || "—"}</div>
                      <div className="text-[#1A1714]/50">{it.isbn || "—"}</div>
                    </div>
                    <div className="col-span-5 font-serif text-base">{it.title} <span className="text-[#1A1714]/50 italic text-xs">— {it.author}</span></div>
                    <div className="col-span-2">{it.format}</div>
                    <div className="col-span-1 text-right">{it.quantity}</div>
                    <div className="col-span-2 text-right">${((it.quantity || 1) * (it.price || 0)).toFixed(2)}</div>
                  </div>
                ))}
              </div>
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
